import {
  assignPrototypeAPIs,
  assignTableAPIs,
  functionalUpdate,
  type Row,
  type RowData,
  type Table_Internal,
  type TableFeature,
  type TableFeatures,
  type Updater,
} from '@tanstack/react-table';

import type {
  DataTableRowOrderState,
  RowOrderFeatureConstructors,
} from './row-order-types.js';
import type { TDataTableFeatures } from '../types/features.types.js';

/**
 * Helper function to update the row order state on the table, which can be called from row-level actions to ensure consistent state management.
 * @internal
 */
export function table_setDraggingRowInfo<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  table: Table_Internal<TFeatures, TData>,
  draggingRowInfo: {
    id: string;
    index: number;
    row: Row<TDataTableFeatures, RowData>;
  } | null,
) {
  // Required cast to ensure precense of the DataTableFeatures.
  (
    table as unknown as Table_Internal<TDataTableFeatures>
  ).baseAtoms.draggingRowInfo.set(draggingRowInfo);
}

/**
 * Helper function to update the row order state on the table, which can be called from row-level actions to ensure consistent state management.
 * @internal
 */
export function table_setRowOrder<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  table: Table_Internal<TFeatures, TData>,
  updater: Updater<DataTableRowOrderState<TFeatures, TData>['rowOrder']>,
) {
  const newValue = functionalUpdate(updater, table.atoms.rowOrder?.get() ?? []);

  queueMicrotask(() => {
    table.options.onRowOrderChange?.(newValue);
  });
}

/**
 * Helper function to access the row model before the row order is applied on the table, which can be called from row-level actions to ensure consistent state management.
 * @internal
 */
export function table_getPreRowOrderRowModel<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>) {
  return table.getExpandedRowModel();
}

/**
 * Helper function to access the row order model on the table, which can be called from row-level actions to ensure consistent state management.
 * @internal
 */
export function table_getRowOrderRowModel<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>) {
  table._rowModels.rowOrderRowModel ??=
    table.options._rowModels?.rowOrderRowModel?.(table);
  if (!table.options.enableRowOrdering || !table._rowModels.rowOrderRowModel) {
    return table.getPreRowOrderRowModel();
  }
  return table._rowModels.rowOrderRowModel();
}

/**
 * Helper function to determine if a specific row can be dragged, based on the table options and the row's properties.
 * @internal
 */
export function row_getCanDrag<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(row: Row<TFeatures, TData>): boolean {
  const { enableRowOrdering, enableRowOrderDragAndDrop } = row.table.options;
  const sortingState = row.table.atoms.sorting?.get();

  if (
    enableRowOrdering !== true ||
    // always disable the drag and drop handle if the rows are sorted
    (sortingState !== undefined && sortingState.length > 0)
  ) {
    return false;
  }

  // check if drag and drop is disabled for the row
  if (
    enableRowOrderDragAndDrop !== undefined &&
    typeof enableRowOrderDragAndDrop === 'function'
  ) {
    return enableRowOrderDragAndDrop(row);
  }
  return true;
}

/**
 * Helper function to determine if a row with disabled drag and drop should be locked in place based on the table options and the row's position in the table.
 * @internal
 */
export function row_getDisabledRowDragAndDropLockPosition<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(row: Row<TFeatures, TData>): boolean | 'start' | 'end' | undefined {
  return row.table.optionsStore.state.lockDisabledRows ?? false;
}

/**
 * Constructs the column row ordering for the table,
 * including API implementations and prototype assignments.
 * @internal
 */
export function constructRowOrderFeature<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(): TableFeature<RowOrderFeatureConstructors<TFeatures, TData>> {
  return {
    getInitialState: (initialState) => {
      return {
        rowOrder: structuredClone([]),
        draggingRowInfo: null,
        ...initialState,
      };
    },
    assignRowPrototype: (prototype, table) => {
      assignPrototypeAPIs('DataTableRowOrderFeature', prototype, table, {
        row_getCanDrag: { fn: (row) => row_getCanDrag(row) },
        row_getDisabledRowDragAndDropLockPosition: {
          fn: (row) => row_getDisabledRowDragAndDropLockPosition(row),
        },
      });
    },
    constructTableAPIs: (table) => {
      assignTableAPIs('DataTableRowOrderFeature', table, {
        table_setDraggingRowInfo: {
          fn: (draggingRowInfo) =>
            table_setDraggingRowInfo(table, draggingRowInfo),
        },
        table_setRowOrder: {
          fn: (
            updater: Updater<
              DataTableRowOrderState<TFeatures, TData>['rowOrder']
            >,
          ) => table_setRowOrder(table, updater),
        },
        table_getPreRowOrderRowModel: {
          fn: () => table_getPreRowOrderRowModel(table),
        },
        table_getRowOrderRowModel: {
          fn: () => table_getRowOrderRowModel(table),
        },
      });
    },
  };
}

/**
 * Feature implementation for Row Order.
 * @internal
 */
export const DataTableRowOrderFeature = constructRowOrderFeature();
